import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
  ResponsiveHeader,
} from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { BankIcon, CreditCardIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { SubHeadline } from '../ui/Typography';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';
import { removeAssetDistribution, type DistributionMap } from '../../utils/distributionUtils';

interface InvestmentAccount {
  id: string;
  type: 'stocks' | 'bonds' | 'mutual_funds' | 'etfs' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  notes?: string;
  documents?: AppDocument[];
}

const ACCOUNT_TYPES = [
  {
    key: 'stocks' as const,
    label: 'Stocks',
    description: 'Individual stock holdings and brokerage accounts',
    Icon: BankIcon,
  },
  {
    key: 'bonds' as const,
    label: 'Bonds',
    description: 'Government and corporate bonds',
    Icon: BankIcon,
  },
  {
    key: 'mutual_funds' as const,
    label: 'Mutual Funds',
    description: 'Professionally managed investment funds',
    Icon: CreditCardIcon,
  },
  {
    key: 'etfs' as const,
    label: 'ETFs',
    description: 'Exchange-traded funds',
    Icon: BankIcon,
  },
  {
    key: 'other' as const,
    label: 'Other Investments',
    description: 'Any other investment accounts',
    Icon: BankIcon,
  },
];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame187: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [accounts, setAccounts] = useState<InvestmentAccount[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState<InvestmentAccount['type'] | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  useEffect(() => {
    if (session?.taskProgress?.financial_assets?.answers) {
      const savedAccounts = session.taskProgress.financial_assets.answers.investmentAccounts || [];
      if (savedAccounts.length > 0) {
        const normalizedAccounts = savedAccounts.map((account: InvestmentAccount) => ({
          ...account,
          documents: normalizeDocuments(account.documents || []),
        }));

        console.log('[Frame187] Loading accounts from session:', normalizedAccounts);
        setAccounts(normalizedAccounts);
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('investment-accounts-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data.length > 0) {
          const normalizedAccounts = data.map((account: InvestmentAccount) => ({
            ...account,
            documents: normalizeDocuments(account.documents || []),
          }));

          console.log('[Frame187] Loading accounts from localStorage backup:', normalizedAccounts);
          setAccounts(normalizedAccounts);
        }
      }
    } catch (error) {
      console.error('[Frame187] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.financial_assets]);

  const persistAccounts = async (updatedAccounts: InvestmentAccount[]) => {
    try {
      localStorage.setItem('investment-accounts-backup', JSON.stringify(updatedAccounts));
      localStorage.setItem(
        'investment-accounts-data-backup',
        JSON.stringify({
          investmentAccounts: updatedAccounts,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('[Frame187] Failed to save to backup:', error);
    }

    await updateTaskProgress('financial_assets', { investmentAccounts: updatedAccounts });
    setAccounts(updatedAccounts);
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame187] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const resetForm = () => {
    setName('');
    setInstitution('');
    setAccountNumber('');
    setNotes('');
    setDocuments([]);
    setEditingAccountId(null);
    setIsUploadingDocuments(false);
  };

  const handleOpenModal = (type: InvestmentAccount['type']) => {
    resetForm();
    setSelectedType(type);
    setShowModal(true);
  };

  const handleEditAccount = (account: InvestmentAccount) => {
    setSelectedType(account.type);
    setName(account.name);
    setInstitution(account.institution);
    setAccountNumber(account.accountNumber);
    setNotes(account.notes || '');
    setEditingAccountId(account.id);
    setDocuments(normalizeDocuments(account.documents || []));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedType || !name || !institution || !accountNumber) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const accountData: InvestmentAccount = {
        id: editingAccountId || Date.now().toString(),
        type: selectedType,
        name: name.trim(),
        institution: institution.trim(),
        accountNumber: accountNumber.trim(),
        notes: notes.trim() || undefined,
        documents,
      };

      const updatedAccounts = editingAccountId
        ? accounts.map((a) => (a.id === editingAccountId ? accountData : a))
        : [...accounts, accountData];

      await persistAccounts(updatedAccounts);
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('[Frame187] Failed to save account:', error);
      alert('Failed to save account. Please try again.');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this account?')) return;

    const accountToDelete = accounts.find((a) => a.id === accountId);
    const attachedDocs = accountToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame187] Failed to delete document while deleting account:', error);
      }
    }

    const updatedAccounts = accounts.filter((a) => a.id !== accountId);

    // Cascade: remove distribution record for the deleted account.
    const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
    if (currentDist[accountId]) {
      const cleanedDist = removeAssetDistribution(currentDist, accountId);
      await updateTaskProgress('assetDistribution', { distributions: cleanedDist });
    }

    await persistAccounts(updatedAccounts);
  };

  const handleRemoveAttachedDocument = async (
    accountId: string,
    documentIndex: number
  ) => {
    const account = accounts.find((a) => a.id === accountId);
    const docToRemove = account?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame187] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedAccounts = accounts.map((account) => {
      if (account.id !== accountId) return account;
      return {
        ...account,
        documents: normalizeDocuments(account.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    await persistAccounts(updatedAccounts);
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame187] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    navigate('/frame-188');
  };

  const handleBack = () => {
    navigate('/frame-186');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Investment Accounts" />

        <SubHeadline className="mb-6">
          Add your investment accounts including stocks, bonds, mutual funds, and ETFs.
          This information will help your loved ones locate and manage these accounts.
        </SubHeadline>

        <div className="max-w-4xl">
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4 mb-8">
            <h3 className="text-sm font-semibold text-teal-800 mb-2">Important Notes</h3>
            <ul className="text-sm text-teal-700 space-y-1">
              <li>• Individual holdings are not listed here.</li>
              <li>• Full portfolios and valuations are accessible through the financial institution.</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {ACCOUNT_TYPES.map((type) => (
              <button
                key={type.key}
                onClick={() => handleOpenModal(type.key)}
                className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
              >
                <div className="flex items-start gap-4">
                  <type.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-stone-800 leading-tight">{type.label}</h3>
                    <p className="text-sm text-stone-600 mt-1">{type.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {accounts.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-medium text-stone-800 mb-4">
                Your Accounts ({accounts.length})
              </h2>

              <div
                className={`space-y-4 ${accounts.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}
              >
                {accounts.map((account) => {
                  const accountType = ACCOUNT_TYPES.find((type) => type.key === account.type);
                  const AccountIcon = accountType?.Icon || BankIcon;

                  return (
                    <div
                      key={account.id}
                      className="bg-white rounded-lg border border-neutral-200 p-4"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                          <AccountIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800 break-words">{account.name}</div>

                            <div className="text-sm text-stone-500 mt-1 break-words">
                              {account.institution}
                              {account.accountNumber &&
                                ` • Account ending in ${account.accountNumber.slice(-4)}`}
                            </div>

                            {account.notes && (
                              <div className="text-sm text-stone-600 mt-2 break-words">{account.notes}</div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                          <button
                            onClick={() => handleEditAccount(account)}
                            className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {account.documents && account.documents.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="text-sm text-cyan-700 font-medium">
                            Attached Documents ({account.documents.length})
                          </div>

                          <div className="space-y-2">
                            {normalizeDocuments(account.documents).map((doc, index) => (
                              <div
                                key={`${account.id}-doc-${doc.id}-${index}`}
                                className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <button
                                  type="button"
                                  onClick={() => openDocument(doc)}
                                  className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                >
                                  <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                  <span
                                    className="text-sm font-medium text-stone-800 truncate"
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </button>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => downloadDocument(doc)}
                                    className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                  >
                                    Download
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachedDocument(account.id, index)}
                                    className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                    aria-label={`Remove ${doc.name}`}
                                    title="Remove document"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    );
                })}
              </div>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Document your investment accounts"
        progressPercent={25}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingAccountId ? 'Edit Account' : 'Add Account'}
        okText={editingAccountId ? 'Update Account' : 'Add Account'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => { 
          setShowModal(false);
          resetForm();
        }}
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Account Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Retirement Portfolio"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Financial Institution *
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Fidelity Investments"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Account Number *
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Any additional information"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>

            <FileUpload
              onUpload={async (newFiles) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame187] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                      <span className="text-sm font-medium text-cyan-700 hover:text-cyan-800 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => removeDocumentFromForm(index)}
                        className="text-red-600 hover:text-red-700 text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame187;